/**
 * Copyright (c) 2013 Marc J. Schmidt
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

// Only used for the dirty checking, so the event callback count is limited to max 1 call per fps per sensor.
// In combination with the event based resize sensor this saves cpu time, because the sensor is too fast and
// would generate too many unnecessary events.
import { debounce } from '@app/utils/common';

let requestAnimationFrame =
  window.requestAnimationFrame ||
  window['mozRequestAnimationFrame'] ||
  window['webkitRequestAnimationFrame'] ||
  function (fn) {
    return window.setTimeout(fn, 20);
  };

/**
 * Get element size
 * @param {HTMLElement} element
 * @returns {Object} {width, height}
 */
function getElementSize(element) {
  if (!element.getBoundingClientRect) {
    return {
      width: element.offsetWidth,
      height: element.offsetHeight,
    };
  }

  let rect = element.getBoundingClientRect();

  return {
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

class EventQueue {
  private q = [];

  get length() {
    return this.q.length;
  }

  add(event) {
    this.q.push(event);
  }

  call(value) {
    this.q.forEach((event) => event.call(this, value));
  }

  remove(event) {
    this.q = this.q.filter((_) => _ !== event);
  }
}

type ExtendedHtmlElement = HTMLElement & {
  resizedAttached?: EventQueue;
  resizeSensor?: HTMLElement & {
    resetSensor: () => any;
  };
};

/**
 * Class for dimension change detection.
 *
 * @param {Element|Element[]|Elements|jQuery} element
 * @param {Function} callback
 *
 * @constructor
 */
export class ResizeSensor {
  constructor(
    private element: ExtendedHtmlElement,
    private callback: (value: { height: number; width: number }) => void,
    private debounce: number = 0
  ) {
    this.attachResizeEvent(element, callback);
  }

  /**
   *
   * @param {HTMLElement} element
   * @param {Function}    resized
   */
  attachResizeEvent(element, resized) {
    if (element.resizedAttached) {
      element.resizedAttached.add(resized);
      return;
    }

    element.resizedAttached = new EventQueue();
    element.resizedAttached.add(resized);

    element.resizeSensor = document.createElement('div');
    element.resizeSensor.dir = 'ltr';
    element.resizeSensor.className = 'resize-sensor';
    let style =
      'pointer-events: none; position: absolute; left: 0px; top: 0px; right: 0; bottom: 0; ' +
      'overflow: hidden; z-index: -1; visibility: hidden; max-width: 100%;';
    let styleChild = 'position: absolute; left: 0; top: 0; transition: 0s;';

    element.resizeSensor.style.cssText = style;
    element.resizeSensor.innerHTML = `<div class="resize-sensor-expand" style="${style}"><div style="${styleChild}"></div></div><div class="resize-sensor-shrink" style="${style}"><div style="${styleChild} width: 200%; height: 200%"></div></div>`;
    element.appendChild(element.resizeSensor);

    let computedStyle = window.getComputedStyle(element);
    let position = computedStyle ? computedStyle.getPropertyValue('position') : null;
    if ('absolute' !== position && 'relative' !== position && 'fixed' !== position) {
      element.style.position = 'relative';
    }

    const expand = element.resizeSensor.childNodes[0];
    const expandChild = expand.childNodes[0];
    const shrink = element.resizeSensor.childNodes[1];
    let dirty;
    let rafId;
    let size = getElementSize(element);
    let lastWidth = size.width;
    let lastHeight = size.height;
    let initialHiddenCheck = true;
    let lastAnimationFrame = 0;

    let resetExpandShrink = function () {
      let width = element.offsetWidth;
      let height = element.offsetHeight;

      expandChild.style.width = width + 10 + 'px';
      expandChild.style.height = height + 10 + 'px';

      expand.scrollLeft = width + 10;
      expand.scrollTop = height + 10;

      shrink.scrollLeft = width + 10;
      shrink.scrollTop = height + 10;
    };

    let reset = function () {
      // Check if element is hidden
      if (initialHiddenCheck) {
        let invisible = element.offsetWidth === 0 && element.offsetHeight === 0;
        if (invisible) {
          // Check in next frame
          if (!lastAnimationFrame) {
            lastAnimationFrame = requestAnimationFrame(function () {
              lastAnimationFrame = 0;

              reset();
            });
          }

          return;
        } else {
          // Stop checking
          initialHiddenCheck = false;
        }
      }

      resetExpandShrink();
    };

    element.resizeSensor.resetSensor = reset;

    let onResized = function () {
      rafId = 0;

      if (!dirty) return;

      lastWidth = size.width;
      lastHeight = size.height;

      if (element.resizedAttached) {
        element.resizedAttached.call(size);
      }
    };

    let onScroll = debounce(
      function () {
        size = getElementSize(element);
        dirty = size.width !== lastWidth || size.height !== lastHeight;

        if (dirty && !rafId) {
          rafId = requestAnimationFrame(onResized);
        }

        reset();
      },
      this.debounce,
      this.debounce === 0
    );

    let addEvent = function (el, name, cb) {
      if (el.attachEvent) {
        el.attachEvent('on' + name, cb);
      } else {
        el.addEventListener(name, cb);
      }
    };

    addEvent(expand, 'scroll', onScroll);
    addEvent(shrink, 'scroll', onScroll);

    // Fix for custom Elements
    requestAnimationFrame(reset);
  }

  detach(ev) {
    if (this.element.resizedAttached) {
      this.element.resizedAttached.remove(ev);
      if (this.element.resizedAttached.length) return;
    }

    if (this.element.resizeSensor) {
      if (this.element.contains(this.element.resizeSensor)) {
        this.element.removeChild(this.element.resizeSensor);
      }

      delete this.element.resizeSensor;
      delete this.element.resizedAttached;
    }
  }

  reset() {
    this.element.resizeSensor.resetSensor();
  }
}
